import type { PermissionService } from "../PermissionService";
import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

export type UserPreferenceVoteItem = Omit<CrudArgs, "@id"> & {
  relatedUser?: string | null
  permissionTemplateGroup?: string | null
  ptgClient?: string | null
  ptgLocation?: string | null
};

export class UserPreferenceVoter extends ContextVoter {
  public permissionService: PermissionService;

  constructor(permissionService: PermissionService) {
    super(permissionService);
    this.permissionService = permissionService;
  }

  private hasContextScopedAccess(
    item: UserPreferenceVoteItem,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if ((item.client || item.location) && this.hasAccess({
      client: item.client,
      location: item.location,
      attribute: contextAttribute,
    })) {
      return true;
    }

    if (!item.permissionTemplateGroup) {
      return false;
    }

    if (!item.ptgClient && !item.ptgLocation) {
      return false;
    }

    return this.hasAccess({
      client: item.ptgClient,
      location: item.ptgLocation,
      attribute: contextAttribute,
    });
  }

  private isOwnUserPreference(item?: UserPreferenceVoteItem): boolean {
    if (!item?.relatedUser || !this.permissionService.userIri) {
      return false;
    }

    return item.relatedUser === this.permissionService.userIri;
  }

  public canReadAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserpreferenceReadany,
    });
  };

  public canCreateAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserpreferenceCreateany,
    });
  };

  public canUpdateAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserpreferenceUpdateany,
    });
  };

  public canDeleteAny = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserpreferenceDeleteany,
    });
  };

  /**
   * Mirrors the API UserPreferenceVoter::canReadViaClient (`GET /clients/{client}/user_preferences` and the
   * preference defaults impact of an organisation): the global read right, or the organisation is readable and
   * `user_preference.read` is held on it.
   */
  public canReadCollectionViaClient = (item: UserPreferenceVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (!item.client || !this.canReadClientContext(item.client)) {
      return false;
    }

    return this.hasAccess({ client: item.client, attribute: PermissionAttributesEnum.ContextOperationUserpreferenceRead });
  };

  /**
   * Mirrors the API UserPreferenceVoter::canReadViaLocation (`GET /locations/{location}/user_preferences` and the
   * preference defaults impact of a location): the global read right, or the location is readable and
   * `user_preference.read` is held on its organisation or on the location itself.
   */
  public canReadCollectionViaLocation = (item: UserPreferenceVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (!item.location || !this.canReadLocationContext(item.client, item.location)) {
      return false;
    }

    return this.hasAccess({
      client: item.client,
      location: item.location,
      attribute: PermissionAttributesEnum.ContextOperationUserpreferenceRead,
    });
  };

  /**
   * Mirrors the API UserPreferenceVoter::canReadViaPtg: the global read right, or the role is readable, belongs to
   * an organisation (`ptgClient` is the role's own organisation) and `user_preference.read` is held on that
   * organisation or on the role's location.
   */
  public canReadCollectionViaPtg = (item: UserPreferenceVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (!item.permissionTemplateGroup || !item.ptgClient) {
      return false;
    }

    if (!this.canReadPermissionTemplateGroupContext(item.ptgClient, item.ptgLocation)) {
      return false;
    }

    return this.hasAccess({
      client: item.ptgClient,
      location: item.ptgLocation,
      attribute: PermissionAttributesEnum.ContextOperationUserpreferenceRead,
    });
  };

  /**
   * Mirrors the API UserPreferenceVoter::canReadInherited: the global read right, the global layer (no scope at
   * all), your own preferences, or `user_preference.read` on the scope.
   */
  public canReadInherited = (item: UserPreferenceVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (item.relatedUser) {
      return this.isOwnUserPreference(item);
    }

    if (!item.client && !item.location && !item.permissionTemplateGroup) {
      return true;
    }

    return this.hasContextScopedAccess(
      item,
      PermissionAttributesEnum.ContextOperationUserpreferenceRead,
    );
  };

  /**
   * Mirrors the API rule for writing your own preference: it must be yours and, when it names a location, that
   * location must be readable.
   */
  private canWriteOwnUserPreference(item: UserPreferenceVoteItem): boolean {
    if (!this.isOwnUserPreference(item)) {
      return false;
    }

    if (!item.location) {
      return true;
    }

    return this.canReadLocationContext(item.client, item.location);
  }

  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    const voteItem = item as UserPreferenceVoteItem | undefined;

    if (this.canCreateAny()) {
      return true;
    }

    if (voteItem?.relatedUser) {
      return this.canWriteOwnUserPreference(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem ?? {},
      PermissionAttributesEnum.ContextOperationUserpreferenceCreate,
    );
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    const voteItem = item as UserPreferenceVoteItem;

    if (this.canUpdateAny()) {
      return true;
    }

    if (voteItem.relatedUser) {
      return this.canWriteOwnUserPreference(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem,
      PermissionAttributesEnum.ContextOperationUserpreferenceUpdate,
    );
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    const voteItem = item as UserPreferenceVoteItem;

    if (this.canDeleteAny()) {
      return true;
    }

    if (voteItem.relatedUser) {
      return this.isOwnUserPreference(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem,
      PermissionAttributesEnum.ContextOperationUserpreferenceDelete,
    );
  };

  public canManage: AggregateVoterCheck<UserPreferenceVoteItem> = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canCreate(item)
      || this.canUpdate(item)
      || this.canDelete(item);
  };

  public canManageForUser = (userIri: string | null | undefined) => {
    if (!userIri) {
      return false;
    }

    if (this.permissionService.userIri === userIri) {
      return true;
    }

    return this.canCreateAny() || this.canUpdateAny();
  };
}
